/**
 * general-chat.service.ts
 * -----------------------
 * Frontend API service for the General Guardrail Chatbot.
 *
 * Endpoints used:
 *   POST /api/v1/general-chat         — full response (non-streaming)
 *   POST /api/v1/general-chat/stream  — SSE streaming response
 *
 * This service is completely independent of any assessment session.
 */

import { Injectable }                    from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError }        from 'rxjs';
import { catchError }                    from 'rxjs/operators';
import { MsalService }                   from '@azure/msal-angular';
import { environment }                   from '../../../environments/environment';

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export interface GeneralChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

export interface GeneralChatRequest {
  query:   string;
  history: GeneralChatMessage[];
}

export interface GeneralChatResponse {
  response: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class GeneralChatService {

  private readonly _base =
    `${environment.apiConfig.uri.replace(/\/$/, '')}/api/v1/general-chat`;

  constructor(
    private readonly http: HttpClient,
    private readonly msal: MsalService,
  ) {}

  /**
   * Stream a general chat response using SSE.
   * Emits text delta strings one at a time as the LLM produces them.
   * Completes when the server sends `data: [DONE]`.
   */
  streamMessage(
    query:   string,
    history: GeneralChatMessage[],
  ): Observable<string> {
    return new Observable<string>(observer => {
      const controller = new AbortController();

      const accounts     = this.msal.instance.getAllAccounts();
      const tokenRequest = {
        scopes:  environment.apiConfig.scopes,
        account: accounts[0] ?? undefined,
      };

      this.msal.instance.acquireTokenSilent(tokenRequest)
        .then(tokenResult => {
          const headers: Record<string, string> = {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${tokenResult.accessToken}`,
          };

          return fetch(`${this._base}/stream`, {
            method:  'POST',
            headers,
            body:    JSON.stringify({ query, history } satisfies GeneralChatRequest),
            signal:  controller.signal,
          });
        })
        .then(async response => {
          if (!response.ok) {
            const errBody = await response.text().catch(() => '');
            observer.error(new Error(`HTTP ${response.status}: ${errBody}`));
            return;
          }

          const reader  = response.body!.getReader();
          const decoder = new TextDecoder();
          let   buffer  = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';

            for (const part of parts) {
              for (const line of part.split('\n')) {
                if (!line.startsWith('data: ')) continue;
                const payload = line.slice(6).trim();
                if (payload === '[DONE]') {
                  observer.complete();
                  return;
                }
                try {
                  const parsed = JSON.parse(payload) as { text: string };
                  if (parsed.text) observer.next(parsed.text);
                } catch { /* ignore malformed lines */ }
              }
            }
          }
          observer.complete();
        })
        .catch(err => {
          if ((err as Error).name !== 'AbortError') {
            observer.error(err);
          }
        });

      return () => controller.abort();
    });
  }

  /**
   * Non-streaming fallback — returns the full response at once.
   */
  sendMessage(
    query:   string,
    history: GeneralChatMessage[],
  ): Observable<GeneralChatResponse> {
    const body: GeneralChatRequest = { query, history };
    return this.http
      .post<GeneralChatResponse>(this._base, body)
      .pipe(catchError(this._handleError));
  }

  private _handleError(err: HttpErrorResponse): Observable<never> {
    const msg =
      err.error?.detail ??
      err.error?.message ??
      err.message ??
      'Chatbot service error.';
    return throwError(() => new Error(msg));
  }
}
